import { NgIf, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { Cart, CartActions, CartLineSources, CartSelectors } from '@next/core-lib/cart';
import { AgrNotificationEmptyModule } from '@next/core-lib/components';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { AgrFormModule } from '@next/core-lib/form';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { RemoveSpecialCharactersPipe } from '@next/core-lib/pipes';
import { TableV2Module } from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';
import { addYears } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, takeUntil } from 'rxjs/operators';

import { ReturnableProductsDataSource } from './datasource/returnable-products.datasource';
import { ReturnableInvoiceLine } from './models/returnable-invoiceline.model';
import { ReturnableInvoiceService } from './services/returnable-invoice.service';

export interface ReturnProductSearchDialogData extends AbstractDialogData {
  cart?: Cart;
}

type DisabledSkus = {
  skuId: number;
  invoiceId: string;
}[];

@Component({
  selector: 'app-return-product-search-dialog',
  templateUrl: './return-product-search-dialog.component.html',
  styleUrls: ['./return-product-search-dialog.component.scss'],
  imports: [
    AgrDialogModule,
    MatFormField,
    MatLabel,
    MatIcon,
    MatPrefix,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    MatIconButton,
    MatSuffix,
    TableV2Module,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    AgrFormModule,
    MatButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    AgrNotificationEmptyModule,
    AsyncPipe,
    AgrI18nSharedModule,
  ],
})
export class ReturnProductSearchDialogComponent extends AbstractDialog<ReturnProductSearchDialogData> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchBar') searchBar: ElementRef;

  public readonly dataSource = new ReturnableProductsDataSource(this._returnableInvoiceService, this.store);
  public readonly searchBarControl = new FormControl();
  public readonly productCount = new BehaviorSubject<number>(0);
  public readonly dateRange = {
    start: addYears(new Date(), -1),
    end: new Date(),
  };
  public readonly tableColumns = ['product', 'quantity', 'invoice', 'addToCart'];
  public quantityForms = new FormArray([]);
  private readonly _disabledSkus = new BehaviorSubject<DisabledSkus>([]);

  public readonly disabledSkus$ = this._disabledSkus.asObservable();
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReturnProductSearchDialogData,
    protected mdDialogRef: MatDialogRef<ReturnProductSearchDialogComponent, AbstractDialogResultData<never>>,
    private readonly store: Store,
    private readonly removeSpecialCharactersPipe: RemoveSpecialCharactersPipe,
    private readonly _returnableInvoiceService: ReturnableInvoiceService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.dataSource
      .connect()
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        this.productCount.next(results.length);
        this.quantityForms.clear();

        results.forEach((line) => {
          this.quantityForms.push(new FormControl(line.returnableQuantity, [Validators.required, Validators.min(1), Validators.max(line.returnableQuantity)]));
        });
      });

    this.searchBarControl.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((value: string) => this.dataSource.applyFilters(this.removeSpecialCharactersPipe.transform(value?.trim())));

    this.populateDisabledSkus();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchBar.nativeElement.focus();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addReturnLineToCart(line: ReturnableInvoiceLine, itemIndex: number) {
    const quantity = this.quantityForms.at(itemIndex).value;
    this.store.dispatch(
      CartActions.addLineToReturnCart({
        cartLine: {
          skuId: line.skuDetails.id,
          quantity: quantity,
          externalInvoiceId: line.externalInvoiceId,
          externalInvoiceLineId: line.externalInvoiceLineId,
          source: CartLineSources.SHOP_RETURN_ORDER,
        },
      }),
    );

    this._disabledSkus.next([...this._disabledSkus.value, { skuId: line.skuDetails.id, invoiceId: line.externalInvoiceId }]);
  }

  clearSearchBar() {
    this.searchBarControl.setValue('');
  }

  doesCartContainItem(cartLine: ReturnableInvoiceLine, disabledSkus: DisabledSkus): boolean {
    return !!disabledSkus.find((x) => x.skuId === cartLine.skuDetails.id && x.invoiceId === cartLine.externalInvoiceId);
  }

  private populateDisabledSkus() {
    if (this.data.cart?.id) {
      this.store
        .select(CartSelectors.selectCart(this.data.cart.id))
        .pipe(
          first(),
          takeUntil(this.destroy$),
          map((cart) => cart?.lines?.map((x) => ({ skuId: x.skuId, invoiceId: x.externalInvoiceId }))),
        )
        .subscribe((disabledSkus) => this._disabledSkus.next([...this._disabledSkus.value, ...disabledSkus]));
    }
  }
}
